/**
 * Scroll reveals and parallax for the brand register, declared with attributes:
 *
 *   data-pg-reveal            fade-up (default) | fade | scale
 *   data-pg-reveal-stagger    on a parent: reveal its children one after another (same values)
 *   data-pg-parallax="0.15"   move an image by 15% of its height while it crosses the viewport
 *
 * Classic deferred script (snippets/pg-scripts.liquid). GSAP (117 KB) is only fetched when the
 * page actually has motion targets, so shop pages don't pay for it. layout/theme.liquid adds
 * html.pg-motion before first paint (CSS hides reveal targets only then) and removes it if GSAP
 * hasn't arrived after 3s, so content can never stay hidden. Reduced motion skips all of this.
 */
(function () {
  const root = document.documentElement;
  const script = /** @type {HTMLScriptElement} */ (document.currentScript);
  const TARGETS = '[data-pg-reveal], [data-pg-reveal-stagger], [data-pg-parallax]';
  /** @type {Promise<void>|null} */
  let loading = null;

  function load(src) {
    return new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = src;
      el.onload = () => resolve();
      el.onerror = reject;
      document.head.append(el);
    });
  }

  function ensureGsap() {
    loading ||= load(script.dataset.gsap).then(() => load(script.dataset.scrolltrigger));
    return loading;
  }

  if (!root.classList.contains('pg-motion')) return;

  function start(scope) {
    ensureGsap()
      .then(() => {
        const { gsap, ScrollTrigger } = window;
        gsap.registerPlugin(ScrollTrigger);
        init(scope);
      })
      .catch(() => root.classList.remove('pg-motion'));
  }

  if (!document.querySelector(TARGETS)) {
    // Nothing to animate on this page; keep listening for sections rendered later.
    root.classList.remove('pg-motion');
  } else {
    start(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector(TARGETS)) start(event.target);
  });
  document.addEventListener('pg:content:rendered', (event) => {
    if (window.gsap && event.target.querySelector?.(TARGETS)) init(event.target);
  });

  const FROM = {
    'fade-up': { opacity: 0, y: 24 },
    fade: { opacity: 0 },
    scale: { opacity: 0, scale: 0.96 },
  };

  function reveal(el, targets, type, stagger) {
    const from = FROM[type] || FROM['fade-up'];
    window.gsap.fromTo(targets, from, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.7,
      ease: 'expo.out',
      stagger,
      clearProps: 'transform,opacity',
      scrollTrigger: { trigger: el, start: 'top 88%', once: true },
      onStart: () => el.classList.add('is-revealed'),
    });
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-pg-reveal]:not(.is-revealed)').forEach((el) => reveal(el, el, el.dataset.pgReveal, 0));
    scope
      .querySelectorAll('[data-pg-reveal-stagger]:not(.is-revealed)')
      .forEach((el) => reveal(el, el.children, el.dataset.pgRevealStagger, 0.06));
    scope.querySelectorAll('[data-pg-parallax]').forEach((el) => {
      const amount = parseFloat(el.dataset.pgParallax) || 0.15;
      const trigger = el.parentElement;
      // Already on screen when the page loads (a top hero): start from where the image is
      // painted (no offset) and only move once the reader scrolls. Starting mid-range would
      // snap the image when GSAP arrives, after first paint.
      const onScreen = trigger.getBoundingClientRect().top < window.innerHeight;
      window.gsap.fromTo(
        el,
        { yPercent: onScreen ? 0 : -amount * 50 },
        {
          yPercent: amount * 50,
          ease: 'none',
          scrollTrigger: { trigger, start: onScreen ? 'top top' : 'top bottom', end: 'bottom top', scrub: true },
        },
      );
    });
  }

})();
